import React, { useState, useRef, ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icons } from "../../icons";
import { NumericInput } from "./NumericInput";

// =============================================================================
// UI COMPONENTS - Reusable Form and Display Components
// =============================================================================

interface FormFieldProps {
  label: string;
  required?: boolean;
  error?: string | null | undefined;
  children: ReactNode;
  hint?: string;
  lockIcon?: ReactNode;
  className?: string;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  required,
  error,
  children,
  hint,
  lockIcon,
  className = "",
}) => (
  <div className={`form-field ${className}`.trim()}>
    <label className="form-label">
      {label}
      {required && <span className="required-marker">*</span>}
      {lockIcon}
    </label>
    {children}
    {hint && <span className="form-hint">{hint}</span>}
    {error && <span className="form-error">{error}</span>}
  </div>
);

interface InputProps {
  type?: string;
  value: string | number | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  className?: string;
  [key: string]: unknown;
}

export const Input: React.FC<InputProps> = ({
  type = "text",
  value,
  onChange,
  placeholder,
  disabled,
  min,
  max,
  step,
  className = "",
  ...props
}) => {
  // For numeric inputs, route through NumericInput so the user can type a
  // decimal point ("1.") without React's controlled-value reformatting
  // wiping it. See CLAUDE.md "NO type=number" hard rule + components/ui/NumericInput.tsx.
  if (type === "number") {
    return (
      <NumericInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        min={
          typeof min === "number"
            ? min
            : min !== undefined
              ? Number(min)
              : undefined
        }
        max={
          typeof max === "number"
            ? max
            : max !== undefined
              ? Number(max)
              : undefined
        }
        className={className}
        {...(props as Record<string, unknown>)}
      />
    );
  }
  return (
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      min={min}
      max={max}
      step={step}
      className={`form-input ${className}`}
      {...props}
    />
  );
};

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  icon?: string;
}

interface SelectProps {
  value: string | undefined;
  onChange: (value: string) => void;
  options: readonly SelectOption[] | readonly string[];
  placeholder?: string;
  disabled?: boolean;
}

export const Select: React.FC<SelectProps> = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    disabled={disabled}
    className="form-select"
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map((opt) => {
      const optValue = typeof opt === "string" ? opt : opt.value;
      const optLabel = typeof opt === "string" ? opt : opt.label;
      return (
        <option key={optValue} value={optValue}>
          {optLabel}
        </option>
      );
    })}
  </select>
);

interface TextAreaProps {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}

export const TextArea: React.FC<TextAreaProps> = ({
  value,
  onChange,
  placeholder,
  rows = 3,
}) => (
  <textarea
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="form-textarea"
  />
);

interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?:
    | "primary"
    | "secondary"
    | "ghost"
    | "success"
    | "warning"
    | "danger";
  size?: "small" | "medium" | "large";
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  icon?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** Accessible label — forwarded to the underlying <button> for screen readers. */
  "aria-label"?: string;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = "primary",
  size = "medium",
  disabled,
  type = "button",
  icon,
  className = "",
  style,
  "aria-label": ariaLabel,
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className={`btn btn-${variant} btn-${size} ${className}`.trim()}
    style={style}
    aria-label={ariaLabel}
  >
    {icon && <span className="btn-icon">{icon}</span>}
    {children}
  </button>
);

interface CardProps {
  children: ReactNode;
  className?: string;
  id?: string;
  title?: ReactNode;
  actions?: ReactNode;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = "",
  id,
  title,
  actions,
  onClick,
}) => (
  <div
    id={id}
    className={`card ${className}`}
    onClick={onClick}
    style={onClick ? { cursor: "pointer" } : undefined}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    {(title || actions) && (
      <div className="card-header">
        {title && <h3 className="card-title">{title}</h3>}
        {actions && <div className="card-actions">{actions}</div>}
      </div>
    )}
    <div className="card-content">{children}</div>
  </div>
);

interface BadgeProps {
  children: ReactNode;
  variant?: "default" | "info" | "warning" | "success" | "error";
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = "default",
}) => <span className={`badge badge-${variant}`}>{children}</span>;

interface StatusBadgeProps {
  status?: string | undefined;
}

const statusVariants: Record<
  string,
  "info" | "warning" | "success" | "error" | "default"
> = {
  Design: "info",
  Planned: "info",
  Permitted: "warning",
  Drilling: "warning",
  Completed: "success",
  Grouted: "success",
  LoopInstalled: "success",
  Commissioning: "info",
  Operational: "success",
  Suspended: "error",
  Abandoned: "error",
  Decommissioned: "error",
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => (
  <Badge variant={status ? statusVariants[status] || "default" : "default"}>
    {status || "Not Set"}
  </Badge>
);

interface TabItem {
  id: string;
  label: string;
  icon?: ReactNode;
}

interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
}

export const Tabs: React.FC<TabsProps> = ({ tabs, activeTab, onTabChange }) => (
  <div className="tabs">
    <div className="tabs-list">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
          onClick={() => onTabChange(tab.id)}
        >
          {tab.icon && <span className="tab-icon">{tab.icon}</span>}
          {tab.label}
        </button>
      ))}
    </div>
  </div>
);

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  size?: "small" | "medium" | "large";
  icon?: ReactNode;
  subtitle?: string;
  badge?: ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = "medium",
  icon,
  subtitle,
  badge,
}) => {
  if (!isOpen) return null;
  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal modal-${size}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          {icon ? (
            <>
              <div className="modal-title-row">
                <span className="modal-title-icon">{icon}</span>
                <h2>{title}</h2>
                {badge}
              </div>
              {subtitle && <span className="modal-subtitle">{subtitle}</span>}
            </>
          ) : (
            <h2 className="modal-title">{title}</h2>
          )}
          <button className="modal-close" onClick={onClose}>
            <Icons.X />
          </button>
        </div>
        <div className="modal-content">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

interface ProgressStep {
  id: string;
  label: string;
}

interface ProgressStepsProps {
  steps: readonly ProgressStep[];
  currentStep: number;
  onStepClick?: (step: number) => void;
}

export const ProgressSteps: React.FC<ProgressStepsProps> = ({
  steps,
  currentStep,
  onStepClick,
}) => (
  <div className="progress-steps">
    {steps.map((step, index) => (
      <div
        key={step.id}
        className={`progress-step ${index <= currentStep ? "completed" : ""} ${index === currentStep ? "active" : ""} ${onStepClick ? "clickable" : ""}`}
        onClick={() => onStepClick && onStepClick(index)}
      >
        <div className="step-indicator">
          {index < currentStep ? <Icons.Check /> : index + 1}
        </div>
        <span className="step-label">{step.label}</span>
        {index < steps.length - 1 && <div className="step-connector" />}
      </div>
    ))}
  </div>
);

interface TableColumn<T> {
  key: string;
  label: string;
  width?: string;
  render?: (value: unknown, row: T) => ReactNode;
}

interface DataTableProps<T extends { id?: string }> {
  columns: TableColumn<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  emptyMessage?: string;
  isLoading?: boolean;
}

export function DataTable<T extends { id?: string }>({
  columns,
  data,
  onRowClick,
  emptyMessage = "No data available",
  isLoading = false,
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div className="data-table-loading">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="data-table-container">
      <table className="data-table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} style={{ width: col.width }}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="empty-message">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, idx) => (
              <tr
                key={row.id || idx}
                onClick={() => onRowClick && onRowClick(row)}
                className={onRowClick ? "clickable" : ""}
              >
                {columns.map((col) => (
                  <td key={col.key}>
                    {col.render
                      ? col.render(
                          (row as Record<string, unknown>)[col.key],
                          row,
                        )
                      : String((row as Record<string, unknown>)[col.key] ?? "")}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

interface FileUploadProps {
  onFileSelect: (
    content: string | ArrayBuffer | null,
    fileName: string,
  ) => void;
  accept?: string;
  label?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  accept = ".csv",
  label = "Upload File",
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0])
      handleFile(e.dataTransfer.files[0]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      onFileSelect(e.target?.result ?? null, file.name);
    };
    reader.readAsText(file);
  };

  return (
    <div
      className={`file-upload ${dragActive ? "drag-active" : ""}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: "none" }}
      />
      <Icons.Upload />
      <span className="file-upload-text">{label}</span>
      <span className="file-upload-hint">
        Drag &amp; drop or click to browse
      </span>
    </div>
  );
};

// Shared lock icon component
export { LockIcon } from "./LockIcon";

// Streaming auto-tab time entry component
export { TimeInput } from "./TimeInput";
export type { TimeInputProps } from "./TimeInput";
export { DateInput } from "./DateInput";
export type { DateInputProps } from "./DateInput";
export { NumericInput } from "./NumericInput";
export type { NumericInputProps } from "./NumericInput";

// Re-export ConfirmDialog components
// eslint-disable-next-line react-refresh/only-export-components
export { ConfirmProvider, useConfirm } from "./ConfirmDialog";

// Re-export Toast components
// eslint-disable-next-line react-refresh/only-export-components
export { ToastProvider, useToast } from "./Toast";
